import { useAuthStore } from "@core/store/storeAuth";
import { fetcherAlegriia } from "@utils/requests";
import { useMemo } from "react";
import useSWR from "swr";
import { useShallow } from "zustand/react/shallow";
import { useStore } from "../..";
import { IFoodTypeFromRestaurant } from "../../types/requests/foodTypes";
import { IRestaurant } from "../../types/requests/restaurant";
import { useUser } from "./useUser";

/**
 * Hook para obtener la información del restaurante asociado a la cuenta que inició sesión.
 *
 * - Obtiene el token y la dirección seleccionada del store global.
 * - Si no hay dirección seleccionada, toma la primera dirección del primer restaurante del usuario.
 * - Realiza una petición para obtener la información del restaurante usando el token y la dirección.
 * - Obtiene los tipos de comida que ofrece el restaurante.
 *
 * Retorna:
 * - restaurant: Información del restaurante y sus tipos de comida.
 * - idAddress: Dirección seleccionada.
 * - mutate: Función para refrescar la información del restaurante.
 * - mutateFoodTypes: Función para refrescar los tipos de comida del restaurante.
 */
export const useRestaurant = () => {
  const { token } = useAuthStore(
    useShallow((state) => ({
      token: state.token,
    })),
  );
  const { idAddress } = useStore(
    useShallow((state) => ({
      idAddress: state.idAddress,
    })),
  );
  const { restaurants } = useUser();

  const address = useMemo(
    () => idAddress || restaurants?.at(0)?.addresses?.at(0)?.idAddress,
    [idAddress, restaurants],
  );

  const { data: infoRestaurant, mutate } = useSWR<IRestaurant[]>(
    !!token &&
      (!!address || !!restaurants?.at(0)?.idRestaurant) && {
        url: "/restaurant",
        auth: token,
        params: address
          ? {
              idAddress: address,
            }
          : { idRestaurant: restaurants?.at(0)?.idRestaurant },
      },
    fetcherAlegriia,
  );
  const restaurant = infoRestaurant?.at(0);

  const { data: foodTypesFromRestaurant, mutate: mutateFoodTypes } = useSWR<
    IFoodTypeFromRestaurant[]
  >(
    !!restaurant?.idRestaurant && {
      url: "/foodTypes",
      params: {
        idRestaurant: restaurant.idRestaurant,
      },
    },
    fetcherAlegriia,
  );

  return useMemo(
    () => ({
      restaurant: restaurant
        ? {
            foodTypes: foodTypesFromRestaurant,
            ...restaurant,
          }
        : undefined,
      idAddress,
      mutate,
      mutateFoodTypes,
    }),
    [foodTypesFromRestaurant, idAddress, mutate, restaurant, mutateFoodTypes],
  );
};
